import type { JsonValue } from "../document/types.ts";
import { cellSourceOf } from "./cell-sources.ts";
import type { FieldAddress } from "./field-kinds.ts";
import type { JsonSchema } from "./schema.ts";

/**
 * Where a cells field's area lies on the map (ADR-021's amendment "the other field kinds", decision 4): its
 * `[column, rowsUp]` collision cells counted from the thing's anchor, the bottom-left corner of the tile its
 * point names (a prop's, where its frame's bottom-left sits, and a marker's alike), columns to the right and
 * rows up. Pure: it reads a thing's point and fields and keeps nothing.
 */

/**
 * One area a thing's fields hold: its address, its title (a list row's with its number), its cells and, when it
 * follows a zone (ADR-021's amendment of 2026-10-02), the zone type it follows.
 */
export interface CellArea {
  readonly address: FieldAddress;
  readonly title: string;
  readonly cells: readonly (readonly [number, number])[];
  readonly zone?: string;
}

/**
 * A thing's anchor in world pixels.
 *
 * @param point The thing's point in tiles.
 * @param tileSize Tile size in pixels.
 * @returns The bottom-left corner of the tile the point names.
 */
export function thingAnchor(point: { readonly x: number; readonly y: number }, tileSize: number): { readonly x: number; readonly y: number } {
  return { x: point.x * tileSize, y: (point.y + 1) * tileSize };
}

/**
 * The cell under a world pixel.
 *
 * @param anchor The thing's anchor in world pixels.
 * @param x World pixel x.
 * @param y World pixel y.
 * @param cellPixels A collision cell's size in pixels.
 * @returns The `[column, rowsUp]` cell.
 */
export function cellAtPixel(anchor: { readonly x: number; readonly y: number }, x: number, y: number, cellPixels: number): [number, number] {
  return [Math.floor((x - anchor.x) / cellPixels), Math.floor((anchor.y - y) / cellPixels)];
}

/**
 * A cell's box in world pixels.
 *
 * @param anchor The thing's anchor in world pixels.
 * @param cell The `[column, rowsUp]` cell.
 * @param cellPixels A collision cell's size in pixels.
 * @returns The box.
 */
export function cellBox(anchor: { readonly x: number; readonly y: number }, cell: readonly [number, number], cellPixels: number): { readonly x: number; readonly y: number; readonly w: number; readonly h: number } {
  return { x: anchor.x + cell[0] * cellPixels, y: anchor.y - (cell[1] + 1) * cellPixels, w: cellPixels, h: cellPixels };
}

/**
 * The pairs a value holds, ignoring anything that is not a pair of integers.
 *
 * @param value A cells value.
 * @returns The cells.
 */
function pairsOf(value: JsonValue | undefined): (readonly [number, number])[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((pair): pair is [number, number] => Array.isArray(pair) && pair.length === 2 && Number.isInteger(pair[0]) && Number.isInteger(pair[1])).map(([c, r]) => [c, r] as const);
}

/**
 * Every area a thing's fields hold: each cells field, and each row of a list of cells, in the schema's order.
 * A cells field holding none is listed empty, so it can be painted; a list lists only its rows. An area that
 * follows a zone is listed with the zone and no cells, since its cells are the zone's to resolve.
 *
 * @param schema The thing's type's schema.
 * @param fields Its field values.
 * @returns The areas.
 */
export function cellAreasOf(schema: JsonSchema, fields: { readonly [key: string]: JsonValue }): readonly CellArea[] {
  const areas: CellArea[] = [];
  for (const [name, property] of Object.entries(schema.properties)) {
    const title = property.title ?? name;
    if (property.type === "cells") {
      areas.push(areaOf({ field: name }, title, fields[name]));
    } else if (property.type === "list" && property.items.type === "cells") {
      const rows = fields[name];
      (Array.isArray(rows) ? rows : []).forEach((row, index) => areas.push(areaOf({ field: name, index }, `${title} ${index + 1}`, row)));
    }
  }
  return areas;
}

/**
 * One area from its value: its pairs, or the zone it follows with none.
 *
 * @param address The field, or one row of it.
 * @param title Its title.
 * @param value Its value.
 * @returns The area.
 */
function areaOf(address: FieldAddress, title: string, value: JsonValue | undefined): CellArea {
  const zone = cellSourceOf(value);
  return zone === null ? { address, title, cells: pairsOf(value) } : { address, title, cells: [], zone };
}
